import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PhoneIcon, MapPinIcon, ClockIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      setFormStatus('success');
    }, 1500);
  };
  return <div className="w-full bg-white" data-id="element-179" data-mp="1qdrkl35u7pcw">
      {/* Header */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-180" data-mp="1qc6rlhz68eh5">
        <div className="max-w-7xl mx-auto text-center" data-id="element-181" data-mp="r0ycuny0m2aw">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-4" data-id="element-182" data-mp="qyc6usqrvuus">
            Contact & <span className="text-brand-red" data-id="element-183" data-mp="czuay7qprc1s">Get a Quote</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg text-steel-300 font-body max-w-2xl mx-auto" data-id="element-184" data-mp="82ccxce8ih8k">
            Fast estimates. Fast turnaround. Tell us about your project and
            we'll get back to you with a clear, no-nonsense quote.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4" data-id="element-185" data-mp="3lfntes4luxlh">
        <div className="max-w-7xl mx-auto" data-id="element-186" data-mp="3litfrzlunldi">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-187" data-mp="3ghchmbur2d0m">
            {/* Left Col: Contact Info */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="lg:col-span-4 space-y-8" data-id="element-188" data-mp="36sysqmymaedi">
              <motion.div variants={fadeInUp} className="bg-steel-50 p-8 border border-steel-100" data-id="element-189" data-mp="2xu3kl5c0iu7k">
                <h2 className="text-2xl font-heading font-bold uppercase text-charcoal mb-6" data-id="element-190" data-mp="31x01mrlwmi9e">
                  Direct Contact
                </h2>
                <div className="space-y-6" data-id="element-191" data-mp="3ujuilejiizi4">
                  <a href="tel:5177778088" className="flex items-start group" data-id="element-192" data-mp="3ulp3isusarm7">
                    <PhoneIcon className="w-6 h-6 text-brand-red mt-1 mr-4 group-hover:scale-110 transition-transform" data-id="element-193" data-mp="3grodqolt8exz" />
                    <div data-id="element-194" data-mp="3kropg1hiyc1c">
                      <p className="font-bold text-charcoal text-lg group-hover:text-brand-red transition-colors" data-id="element-195" data-mp="ce82kx2bczd6">
                        (517) 777-8088
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-196" data-mp="h6j3a24vop6s">
                        Primary Phone (Call for fastest response)
                      </p>
                    </div>
                  </a>
                  <div className="flex items-start" data-id="element-197" data-mp="1wu63sci1wf1t">
                    <MapPinIcon className="w-6 h-6 text-brand-red mt-1 mr-4" data-id="element-198" data-mp="2bjvbkqknfhb2" />
                    <div data-id="element-199" data-mp="26laayq1hivm3">
                      <p className="font-bold text-charcoal text-lg" data-id="element-200" data-mp="26n4srvhgm7ly">
                        Fowlerville, MI
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-201" data-mp="1iy2dguaasqum">
                        Serving the entire state of Michigan
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-202" data-mp="1ss2fuoe73hzn">
                    <ClockIcon className="w-6 h-6 text-brand-red mt-1 mr-4" data-id="element-203" data-mp="1nuamdpxwjntc" />
                    <div data-id="element-204" data-mp="3492gq8elz55a">
                      <p className="font-bold text-charcoal text-lg" data-id="element-205" data-mp="34897n0s6g3jz">
                        Business Hours
                      </p>
                      <p className="text-sm text-steel-500" data-id="element-206" data-mp="2zclmcwgtfujr">
                        Mon-Fri: 8:00 AM - 6:00 PM
                        <br data-id="element-207" data-mp="1dsgqv4p4lwvi" />
                        Sat: By Appointment
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-charcoal p-8 text-white" data-id="element-208" data-mp="1hwemv6j5mrkc">
                <h3 className="text-xl font-heading font-bold uppercase mb-4" data-id="element-209" data-mp="1rt18hxjb1mnf">
                  Mobile Service
                </h3>
                <p className="text-steel-300 text-sm leading-relaxed mb-6" data-id="element-210" data-mp="1wprlud3q6455">
                  We are a fully mobile operation. We bring our dustless and dry
                  blasting equipment directly to your site, facility, or home
                  anywhere in Michigan.
                </p>
                <a href="https://www.facebook.com/meremblasting/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-brand-red font-bold hover:text-white transition-colors" data-id="element-211" data-mp="2akalwhw74vka">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true" data-id="element-212" data-mp="2kl34c1141643">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" data-id="element-213" data-mp="2bnssoekdreyn" />
                  </svg>
                  Message on Facebook
                </a>
              </motion.div>
            </motion.div>

            {/* Right Col: Quote Form */}
            <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="lg:col-span-8" data-id="element-214" data-mp="3mb897oyh6fb3">
              <div className="bg-white border border-steel-200 p-6 md:p-10 shadow-sm" data-id="element-215" data-mp="1pdu5db713n21">
                <h2 className="text-3xl font-heading font-extrabold uppercase text-charcoal mb-2" data-id="element-216" data-mp="1ap7r7ct511ce">
                  Request a Free Estimate
                </h2>
                <p className="text-steel-500 mb-8" data-id="element-217" data-mp="1floey5wfddoy">
                  Fill out the details below and we'll get back to you within
                  24-48 hours.
                </p>

                {formStatus === 'success' ? <div className="bg-steel-50 border border-green-200 p-8 text-center rounded-sm" data-id="element-218" data-mp="rq27sg426jtx">
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" data-id="element-219" data-mp="won6gb3nwvg9" />
                    <h3 className="text-2xl font-heading font-bold text-charcoal mb-2" data-id="element-220" data-mp="2m3tnnxczdzxt">
                      Request Sent Successfully
                    </h3>
                    <p className="text-steel-600" data-id="element-221" data-mp="2r2efp9i2xdb6">
                      Thank you for reaching out. We've received your project
                      details and will be in touch shortly with your estimate.
                    </p>
                    <Button variant="primary" className="mt-8" onClick={() => setFormStatus('idle')} data-id="element-222" data-mp="p5dxuk2zd0s9">
                      Submit Another Request
                    </Button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-223" data-mp="u4h1q17850yn">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-224" data-mp="u6jswok4504y">
                      <div data-id="element-225" data-mp="183q8695mogpf">
                        <label htmlFor="name" className="block text-sm font-bold text-charcoal mb-2" data-id="element-226" data-mp="181vy5pvwa904">
                          Full Name *
                        </label>
                        <input type="text" id="name" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="John Doe" data-id="element-227" data-mp="34xjyfx6zk9t0" />
                      </div>
                      <div data-id="element-228" data-mp="2xrpm3eletjox">
                        <label htmlFor="phone" className="block text-sm font-bold text-charcoal mb-2" data-id="element-229" data-mp="2xu3f7hwj4ltu">
                          Phone Number *
                        </label>
                        <input type="tel" id="phone" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="(555) 123-4567" data-id="element-230" data-mp="2a1mtmxdl2bps" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-231" data-mp="19uhs8cqgd9aa">
                      <div data-id="element-232" data-mp="19v9lvi0howxx">
                        <label htmlFor="email" className="block text-sm font-bold text-charcoal mb-2" data-id="element-233" data-mp="153rkisuk0ucf">
                          Email Address
                        </label>
                        <input type="email" id="email" className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="john@example.com" data-id="element-234" data-mp="v753kf5cmnfo" />
                      </div>
                      <div data-id="element-235" data-mp="2ghlle2ksvv7k">
                        <label htmlFor="location" className="block text-sm font-bold text-charcoal mb-2" data-id="element-236" data-mp="2ggu13nwnljwl">
                          Project City / Location *
                        </label>
                        <input type="text" id="location" required className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors" placeholder="e.g. Lansing, MI" data-id="element-237" data-mp="sfirkxzte1kf" />
                      </div>
                    </div>

                    <div data-id="element-238" data-mp="ngz5mxl80ps2">
                      <label htmlFor="service" className="block text-sm font-bold text-charcoal mb-2" data-id="element-239" data-mp="ni93pms2pxyp">
                        Primary Service Needed
                      </label>
                      <select id="service" className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors appearance-none" data-id="element-240" data-mp="3r3y2cnblpgzw">
                        <option value="" data-id="element-241" data-mp="3htlgyy3um6sr">Select a service...</option>
                        <option value="dustless" data-id="element-242" data-mp="333xevunuf43z">Dustless Blasting</option>
                        <option value="dry" data-id="element-243" data-mp="3815wnu52x64p">Dry Blasting</option>
                        <option value="auto" data-id="element-244" data-mp="37zcpmdqk0jw6">Automotive Restoration</option>
                        <option value="heavy-equip" data-id="element-245" data-mp="3vs1mwmtvzt9u">
                          Heavy Equipment Cleaning
                        </option>
                        <option value="graffiti" data-id="element-246" data-mp="3lt3zjn8765sf">Graffiti Removal</option>
                        <option value="concrete" data-id="element-247" data-mp="3lv6oe3xm5svk">
                          Concrete/Brick Restoration
                        </option>
                        <option value="marine" data-id="element-248" data-mp="2adffb3mbvkvb">Boat/Marine Prep</option>
                        <option value="other" data-id="element-249" data-mp="2ag9xspls6vm3">Other / Unsure</option>
                      </select>
                    </div>

                    <div data-id="element-250" data-mp="lut1d5erae4f">
                      <label htmlFor="message" className="block text-sm font-bold text-charcoal mb-2" data-id="element-251" data-mp="lt9nflgkof5t">
                        Project Details *
                      </label>
                      <textarea id="message" required rows={5} className="w-full px-4 py-3 bg-steel-50 border border-steel-200 focus:border-brand-red focus:ring-1 focus:ring-brand-red outline-none transition-colors resize-y" placeholder="Tell us about the surface, what needs to be removed, and your ideal timeline..." data-id="element-252" data-mp="c257ojj2oq9x"></textarea>
                    </div>

                    <Button type="submit" variant="primary" size="lg" className="w-full md:w-auto" disabled={formStatus === 'submitting'} data-id="element-253" data-mp="3ke9rav8u9ruy">
                      {formStatus === 'submitting' ? 'Sending Request...' : 'Get My Free Quote'}
                    </Button>
                    <p className="text-xs text-steel-400 mt-4" data-id="element-254" data-mp="1zuxjfvdengt2">
                      By submitting this form, you agree to be contacted by
                      Merem Blasting regarding your project. We do not share
                      your information.
                    </p>
                  </form>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}